import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.role !== "owner" && data.role !== "staff") {
                await api.post("/auth/logout").catch(() => {});
                setError("This login is for store staff only. Customers sign in from the account icon in the menu.");
                return;
            }
            navigate("/admin");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail, "Login failed. Check your details and try again."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main data-testid="admin-login-page" className="min-h-[70vh] bg-slate-50 flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                <div className="flex justify-center mb-6">
                    <img src="/assets/logo.webp" alt="Freddie's Afro Caribbean Store logo" className="h-12 w-auto rounded-md" />
                </div>
                <h1 className="font-heading text-2xl font-extrabold text-slate-900 text-center" data-testid="admin-login-heading">
                    Staff Login
                </h1>
                <p className="text-sm text-slate-500 text-center mt-1 mb-6">Store staff only — manage the shop from here. Customers: use the account icon in the menu.</p>

                {error && (
                    <p className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" data-testid="admin-login-error" role="alert">
                        {error}
                    </p>
                )}

                <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                    <div className="space-y-1.5">
                        <Label htmlFor="admin-email">Email</Label>
                        <Input
                            id="admin-email"
                            type="email"
                            required
                            autoComplete="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="admin-email-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="admin-password">Password</Label>
                        <div className="relative">
                            <Input
                                id="admin-password"
                                type={showPassword ? "text" : "password"}
                                required
                                autoComplete="current-password"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-password-input"
                                className="pr-11"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                data-testid="admin-password-toggle"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0033A0]"
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        <p className="text-xs text-slate-400">
                            Tip: tap the eye to check exactly what's in the box — password managers sometimes fill an old password.
                        </p>
                    </div>
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="admin-login-submit"
                        className="w-full rounded-full bg-[#0033A0] hover:bg-[#002277] text-white font-heading font-bold"
                    >
                        {busy ? "Signing in…" : "Sign In"}
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm">
                    <Link to="/" className="text-[#0033A0] font-bold hover:underline" data-testid="admin-login-back">
                        Back to the website
                    </Link>
                </p>
            </div>
        </main>
    );
}
